import { useCallback, useState } from 'react';
import { ListGroup, Progress } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardList, faClipboardQuestion, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { faClipboard } from '@fortawesome/free-regular-svg-icons';
import { useFormValues } from '@s20/components/forms/FormContext';
import { Hide } from '@s20/components/common/Hide';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../../../Common';
import { Flex } from '../../../common/forms/FormElements';
import { useResourceQueryKey } from '../common/useResourceQueryKey.hook';
import { Resource } from './Resource';
import { Toolbar } from './Toolbar';
import { useTotalAvailableResources } from './useTotalAvailableResources.hook';

export const Resources = ({ selected, setSelected }) => {
    const {
        selectedJobId, selectedDate: date, selectedResourceType, filters,
        jobsLoading, replacementAssignmentId, replacementOriginalResourceId, replacementLinkedAssignmentIds,
    } = useFormValues();

    const totalAvailable = useTotalAvailableResources();

    const handleSetSelected = useCallback((resource) => {
        if (selected.some((sel) => sel.id === resource.id)) {
            setSelected(selected.filter((sel) => sel.id !== resource.id));
        } else if (selected.length < totalAvailable) {
            setSelected(selected.concat(resource));
        }
    }, [selected, setSelected, totalAvailable]);

    const {
        selectedResourceGroupId, isRentalPickup, selectedCertifications, dispatchGroupId, ...query
    } = useResourceQueryKey();

    const { data, isLoading } = useQuery({
        ...query,
        structuralSharing: false,
        queryFn: () => httpClient.post(
            ApiRoutes.dispatch.resourcesForJobDate(),
            {
                selectedJobId, selectedDate: moment(date).format('YYYY-MM-DD'), selectedResourceType, filters, selectedResourceGroupId,
                selectedCertifications: selectedCertifications ?? [],
                isRentalPickup, dispatchGroupId,
                replacementLinkedAssignmentIds: replacementLinkedAssignmentIds ?? [],
            },
            httpClient.format.json,
        ),
        enabled: Boolean(date && selectedJobId && selectedResourceType && selectedResourceGroupId && totalAvailable > 0),
        // While replacing an assignment, the resource being replaced can't be picked as its
        // own replacement.
        select: (resources) => (replacementAssignmentId && replacementOriginalResourceId
            ? resources?.filter((resource) => resource.id !== replacementOriginalResourceId)
            : resources),
    });

    return (
        <div className="h-100 d-flex flex-column">
            {Boolean(isLoading) && <Progress />}

            {selectedJobId ? (
                <>
                    {Boolean(selectedResourceGroupId) && (
                        <Hide mdUp>
                            <Toolbar
                                key={`${selectedJobId}${selectedResourceGroupId}`}
                                selected={selected}
                                setSelected={setSelected}
                                totalAvailable={totalAvailable}
                            />
                        </Hide>
                    )}

                    {(data || []).length > 0 && (
                        <ListGroup className="flex-fill overflow-auto">
                            {data.map((resource) => (
                                <Resource
                                    key={resource.id}
                                    isSelected={selected.some((sel) => sel.id === resource.id)}
                                    setSelected={handleSetSelected}
                                    {...resource}
                                />
                            ))}
                        </ListGroup>
                    )}

                    {!isLoading && selectedJobId && isEmpty(data) && (
                        <Flex
                            row={false}
                            alignItems="center"
                            justifyContent="center"
                        >
                            <FontAwesomeIcon
                                icon={selectedResourceGroupId ? faClipboardQuestion : faClipboardList}
                                size="5x"
                                className="text-muted mb-4 font-weight-light"
                            />

                            <h5 className="text-muted font-weight-light">
                                {selectedResourceGroupId ? 'No results found' : 'Select a resource to load matching staff or equipment'}
                            </h5>
                        </Flex>
                    )}
                </>
            ) : (
                <Flex
                    row={false}
                    alignItems="center"
                    justifyContent="center"
                >
                    <FontAwesomeIcon
                        icon={jobsLoading ? faSpinner : faClipboard}
                        size="5x"
                        className="text-muted mb-4 font-weight-light"
                        spin={jobsLoading}
                    />

                    <h5 className="text-muted font-weight-light">
                        {jobsLoading ? 'Loading jobs...' : 'Select a job to get started'}
                    </h5>
                </Flex>
            )}
        </div>
    );
};

export const ResourcesContainer = () => {
    const [selected, setSelected] = useState([]);

    return (
        <Resources
            selected={selected}
            setSelected={setSelected}
        />
    );
};